import { type FC, type ReactNode, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Alert,
  Badge,
  Column,
  FormField,
  NumberInput,
  Radio,
  RadioGroup,
  Row,
  Select,
} from "@hightouch/htui";

import { Card } from "src/components/card";
import {
  DEFAULT_EXPIRE_AFTER,
  DEFAULT_WAIT_FOR,
  type ExpireAfter,
  formatExpireAfter,
  WORKSPACE_EXPIRY_DEFAULT,
} from "src/components/journey/expire-after";
import { ExpireAfterInput } from "src/components/journey/expire-after-input";
import { DEFAULT_SYNC_TIMEOUT_ACTION } from "@hightouch/lib/journeys/types/config-types";
import { useUser } from "src/contexts/user-context";
import { TIME_OPTIONS } from "src/constants";
import { JourneyNodeSyncMode } from "src/types/journeys";
import { IntervalUnit } from "src/types/visual";
import { useFormkitContext } from "src/formkit/components/formkit-context";
import type { OverrideConfig } from "src/components/destinations/types";
import { useDestinationDefinitionQuery } from "src/graphql";
import { PageSpinner } from "src/components/loading";
import { Link } from "src/router";
import isEmpty from "lodash/isEmpty";

const getFieldName = (namePrefix: string, fieldName: string) => {
  return `${namePrefix ? namePrefix + "." : ""}${fieldName}`;
};

// Sanity catalog categories for destinations that send emails, SMS, or push notifications, where a Trigger resend reaches a person.
const MESSAGING_DESTINATION_CATEGORIES = new Set(["Email", "SMS & push"]);

interface JourneySyncConfigProps {
  /** Field name prefix for form fields */
  namePrefix?: string;
  /** Whether the form fields should be disabled */
  isDisabled?: boolean;
  /** Whether the form is loading underlying data */
  isLoading?: boolean;
  /** Whether to show the journey exit warning for certain conditions */
  isJourneyExitWarningEligible?: boolean;
  /** Display mode: 'card' for Card wrapper styling, 'inline' for custom column styling */
  variant?: "card" | "inline";
  /** Right-aligned content for inline variant header (e.g., clearable/lock buttons) */
  rightContent?: ReactNode;
  /** Destination type slug, used to look up catalog categories when the formkit context has no definition */
  destinationType?: string;
  /** Sync template configuration for field visibility filtering */
  syncTemplate?: {
    id?: number;
    config?: Record<string, unknown>;
    override_config?: OverrideConfig;
    journey_settings?: Record<string, unknown>;
  } | null;
  /** Whether any sync in the surrounding journey uses Trigger mode */
  journeyHasTriggerSync?: boolean;
}

export const JourneySyncConfig: FC<JourneySyncConfigProps> = ({
  namePrefix = "",
  isDisabled = false,
  isLoading = false,
  isJourneyExitWarningEligible = false,
  variant = "card",
  rightContent,
  destinationType,
  syncTemplate,
  journeyHasTriggerSync = false,
}) => {
  const form = useFormContext();
  const {
    journeySyncExpiryEnabled,
    journeyMessageSyncsEnabled,
    journeyTriggerSyncsDeprecated,
  } = useFlags();
  const { workspace } = useUser();
  const workspaceDefault =
    (workspace?.journey_sync_expiry_default as ExpireAfter | null) ??
    WORKSPACE_EXPIRY_DEFAULT;
  const formkitContext = useFormkitContext();
  const journeySettings = formkitContext?.journeySettings;
  const formkitDestinationDefinition = formkitContext?.destinationDefinition;
  const destinationDefinitionQuery = useDestinationDefinitionQuery(
    { slug: destinationType ?? "" },
    {
      enabled: !formkitDestinationDefinition && Boolean(destinationType),
      select: (data) => data.getDestinationDefinition,
    },
  );
  const isMessagingDestination = Boolean(
    (
      formkitDestinationDefinition ?? destinationDefinitionQuery.data
    )?.categories.some(
      (category) =>
        category != null && MESSAGING_DESTINATION_CATEGORIES.has(category.name),
    ),
  );

  // Get current values, with fallback to journey settings from template
  // Only use template fallback if form value is undefined (not set), not if it's null (explicitly cleared)
  const formMode = form.watch(getFieldName(namePrefix, "mode"));
  const currentMode = formMode !== undefined ? formMode : journeySettings?.mode;

  const formExitConfig = form.watch(getFieldName(namePrefix, "exit_config"));
  const currentExitConfig =
    formExitConfig !== undefined
      ? formExitConfig
      : journeySettings?.exit_config;

  const isCohortSync = currentMode === JourneyNodeSyncMode.Cohort;
  const isTriggerSync = currentMode === JourneyNodeSyncMode.Trigger;
  const isMessageSync = currentMode === JourneyNodeSyncMode.Message;
  const showExpireAfterConfig = isTriggerSync && journeySyncExpiryEnabled;

  // Captured on mount so a saved Trigger sync keeps the option after the user picks another mode.
  const [initialMode] = useState(currentMode);
  const showTriggerOption =
    !journeyTriggerSyncsDeprecated ||
    journeyHasTriggerSync ||
    initialMode === JourneyNodeSyncMode.Trigger;

  const isExplicitlyUnlocked =
    syncTemplate?.override_config?.journey_settings?.overridable === true;
  const templateHasJourneySettings =
    syncTemplate && !isEmpty(syncTemplate.journey_settings);
  // Journey settings should always be visible, but locked/disabled when the
  // template has values and not marked as overridable
  const isFieldDisabledByTemplate = Boolean(
    !isExplicitlyUnlocked && templateHasJourneySettings,
  );

  const handleModeChange = (value: JourneyNodeSyncMode) => {
    form.setValue(getFieldName(namePrefix, "mode"), value);

    const isMessage = value === JourneyNodeSyncMode.Message;
    form.setValue(
      getFieldName(namePrefix, "exit_config"),
      {
        remove_after: null,
        remove_on_journey_exit:
          value === JourneyNodeSyncMode.Cohort ? false : null,
        expire_after: null,
        wait_for: isMessage ? { ...DEFAULT_WAIT_FOR } : null,
        on_timeout: isMessage ? DEFAULT_SYNC_TIMEOUT_ACTION : null,
        send_in_run: isMessage ? false : null,
      },
      { shouldDirty: true },
    );
  };

  const renderModeSelection = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "mode")}
      defaultValue={currentMode}
      render={({ field }) => (
        <FormField label="What type of sync is this?">
          <RadioGroup
            isDisabled={isDisabled || isFieldDisabledByTemplate}
            orientation="vertical"
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              handleModeChange(value);
            }}
          >
            {journeyMessageSyncsEnabled && (
              <Radio
                value={JourneyNodeSyncMode.Message}
                label="Message"
                description="For sending messages through the destination. Each profile is synced at most once per journey entry and stays in this tile until the destination accepts their row."
              />
            )}
            <Radio
              value={JourneyNodeSyncMode.Cohort}
              label="Cohort"
              description="For syncing users into a cohort or list. Users will never appear in the destination cohort more than one time and you can specify when the user should be removed."
            />
            {showTriggerOption && (
              <Radio
                value={JourneyNodeSyncMode.Trigger}
                label="Trigger"
                badge={<Badge>Deprecated</Badge>}
                description={
                  <>
                    For triggering campaigns or sending user events. The same
                    user can be synced multiple times and a removal is not
                    necessary.{" "}
                    <Link
                      href={`${import.meta.env.VITE_DOCS_URL}/customer-studio/journeys/tiles#trigger-mode-deprecation`}
                      isExternal
                    >
                      Learn why Trigger is deprecated
                    </Link>
                  </>
                }
              />
            )}
          </RadioGroup>
        </FormField>
      )}
    />
  );

  const renderTriggerWarning = () => (
    <Alert
      type={isMessagingDestination ? "error" : "warning"}
      title="Trigger syncs can send duplicate messages"
      message={`If a profile leaves and rejoins the parent model, they can get the same message again.${
        journeyMessageSyncsEnabled
          ? " To send an email, SMS, or push notification, use a Message sync instead, which sends at most once per journey entry."
          : ""
      }`}
    />
  );

  const renderWaitForConfig = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "exit_config.wait_for")}
      defaultValue={currentExitConfig?.wait_for ?? { ...DEFAULT_WAIT_FOR }}
      render={({ field }) => (
        <FormField
          label="How long should profiles wait for the send?"
          description="Profiles stay in this tile until the destination accepts their row or this much time passes."
        >
          <ExpireAfterInput
            isDisabled={isDisabled || isFieldDisabledByTemplate}
            value={field.value}
            onChange={field.onChange}
          />
        </FormField>
      )}
    />
  );

  const renderOnTimeoutConfig = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "exit_config.on_timeout")}
      defaultValue={
        currentExitConfig?.on_timeout ?? DEFAULT_SYNC_TIMEOUT_ACTION
      }
      render={({ field }) => (
        <FormField label="What should happen when the wait ends without a send?">
          <RadioGroup
            isDisabled={isDisabled || isFieldDisabledByTemplate}
            orientation="vertical"
            value={field.value ?? DEFAULT_SYNC_TIMEOUT_ACTION}
            onChange={field.onChange}
          >
            <Radio value="continue" label="Continue through the journey" />
            <Radio value="exit" label="Exit the journey" />
          </RadioGroup>
        </FormField>
      )}
    />
  );

  const renderSendInRunConfig = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "exit_config.send_in_run")}
      defaultValue={currentExitConfig?.send_in_run ?? false}
      render={({ field }) => (
        <FormField label="When should profiles move to the next tile?">
          <RadioGroup
            isDisabled={isDisabled || isFieldDisabledByTemplate}
            orientation="vertical"
            value={field.value === true}
            onChange={field.onChange}
          >
            <Radio
              value={false}
              label="On the next journey run, after the send is confirmed"
            />
            <Radio
              value={true}
              label="In the same journey run, once the send finishes"
            />
          </RadioGroup>
        </FormField>
      )}
    />
  );

  const renderRemoveAfterConfig = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "exit_config.remove_after")}
      defaultValue={currentExitConfig?.remove_after}
      render={({ field }) => (
        <FormField label="Should users be removed from this sync after a specified time period?">
          <RadioGroup
            isDisabled={isDisabled || isFieldDisabledByTemplate}
            orientation="vertical"
            value={field.value !== null}
            onChange={(value) => {
              field.onChange(
                value ? { quantity: 1, unit: IntervalUnit.Day } : null,
              );
            }}
          >
            <Radio value={false} label="No, don't remove" />
            <Row align="center" gap={2}>
              <Radio value={true} label="Yes, remove after" />
              {field.value && (
                <>
                  <NumberInput
                    isDisabled={isDisabled || isFieldDisabledByTemplate}
                    placeholder="Select quantity…"
                    value={field.value?.quantity}
                    width="auto"
                    onChange={(value) =>
                      field.onChange({
                        ...field.value,
                        quantity: value,
                      })
                    }
                  />
                  <Select
                    isDisabled={isDisabled || isFieldDisabledByTemplate}
                    placeholder="Select delay unit…"
                    options={TIME_OPTIONS}
                    value={field.value?.unit}
                    width="auto"
                    onChange={(value) =>
                      field.onChange({ ...field.value, unit: value })
                    }
                  />
                </>
              )}
            </Row>
          </RadioGroup>
        </FormField>
      )}
    />
  );

  const renderExitConfig = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "exit_config.remove_on_journey_exit")}
      defaultValue={currentExitConfig?.remove_on_journey_exit || false}
      render={({ field }) => (
        <>
          <FormField label="Should users be removed from this sync when they exit the journey?">
            <RadioGroup
              isDisabled={isDisabled || isFieldDisabledByTemplate}
              orientation="vertical"
              value={field.value == true}
              onChange={(value) => {
                field.onChange(value || null);
              }}
            >
              <Radio value={false} label="No, don't remove" />
              <Radio value={true} label="Yes, remove upon exit" />
            </RadioGroup>
          </FormField>

          {isJourneyExitWarningEligible && field.value && (
            <Alert
              type="warning"
              title="Users might never be synced"
              message="After this tile, there's at least one path to completing the journey that has no time delays. Users who go down this path will immediately exit the journey after advancing from this tile, and thus be removed from the sync before it ever has a chance to run. You may want to change your selection from 'Yes, remove upon exit' or switch to a 'Trigger' sync."
            />
          )}
        </>
      )}
    />
  );

  const renderExpireAfterConfig = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "exit_config.expire_after")}
      defaultValue={currentExitConfig?.expire_after ?? null}
      render={({ field }) => (
        <FormField
          label="Entry expiry"
          description="Entries waiting longer than this window are skipped instead of sent."
        >
          <RadioGroup
            isDisabled={isDisabled || isFieldDisabledByTemplate}
            orientation="vertical"
            value={field.value != null}
            onChange={(value) => {
              field.onChange(value ? { ...DEFAULT_EXPIRE_AFTER } : null);
            }}
          >
            <Radio
              value={false}
              label={`Use the workspace default (${formatExpireAfter(workspaceDefault)})`}
            />
            <Row align="center" gap={2}>
              <Radio value={true} label="Override for this sync" />
              {field.value && (
                <ExpireAfterInput
                  isDisabled={isDisabled || isFieldDisabledByTemplate}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            </Row>
          </RadioGroup>
        </FormField>
      )}
    />
  );

  if (isLoading) {
    return <PageSpinner />;
  }

  if (variant === "card") {
    // "Card" variant - used within the journeys canvas
    return (
      <Column gap={6}>
        <Card gap={4}>
          {renderModeSelection()}
          {isTriggerSync && renderTriggerWarning()}
        </Card>
        {isCohortSync && (
          <>
            <Card>{renderRemoveAfterConfig()}</Card>
            <Card gap={4}>{renderExitConfig()}</Card>
          </>
        )}
        {showExpireAfterConfig && <Card>{renderExpireAfterConfig()}</Card>}
        {isMessageSync && (
          <>
            <Card>{renderWaitForConfig()}</Card>
            <Card>{renderOnTimeoutConfig()}</Card>
            <Card>{renderSendInRunConfig()}</Card>
          </>
        )}
      </Column>
    );
  }

  // Inline mode - similar to the original journey-settings styling
  return (
    <Column
      gap={6}
      backgroundColor="white"
      borderWidth="hairline"
      borderStyle="solid"
      borderColor="base.border"
      borderRadius="md"
      p={6}
    >
      {rightContent ? (
        <Row justify="space-between" align="flex-start">
          {renderModeSelection()}
          {rightContent}
        </Row>
      ) : (
        renderModeSelection()
      )}
      {isTriggerSync && renderTriggerWarning()}
      {isCohortSync && renderRemoveAfterConfig()}
      {isCohortSync && renderExitConfig()}
      {showExpireAfterConfig && renderExpireAfterConfig()}
      {isMessageSync && renderWaitForConfig()}
      {isMessageSync && renderOnTimeoutConfig()}
      {isMessageSync && renderSendInRunConfig()}
    </Column>
  );
};
